/**
 * QuietModeManager — Lever 6 (Jun 3, 2026, session v5.15)
 *
 * Single source of truth for the user-controlled "quiet mode" preference.
 * When quiet mode is ON, the user has explicitly opted out of LLM-drafted
 * prose synthesis. The backend still returns web links, images, KG
 * entities, RAG passages, and citation map — the LLM call is short-circuited
 * server-side (see backend/server.py 'LEVER 6' marker).
 *
 * User activates / deactivates the mode by typing one of a small set of
 * natural-language commands into the query field. Commands match the
 * ENTIRE query string (after lowercase + trim) so legitimate queries
 * containing the same words pass through untouched.
 *
 * Persistence: localStorage only for Beta. Per-device. Future: optional
 * Mongo persistence via beta_users.preferences.
 *
 * Verbatim copy per Copy Guard-Rail (SESSION_GUARDRAIL.md §3):
 *   - First-time tip and confirmation toasts are operator-approved
 *     verbatim strings. Do NOT rephrase without operator approval.
 *
 * Reference: /app/memory/PRD.md "Session Open (v5.15)" + R-009 in
 * /app/memory/QA_WORKFLOW.md (future R-010 entry pending close).
 */

// All command phrases — checked as full-string match after lowercase+trim.
// Adding new phrases here is safe (additive). Removing phrases is a
// behaviour change and must surface in PRD + tests.
export const QUIET_ON_PHRASES = [
  'stop talking so much',
  'stop talking',
  'quiet mode',
  'links only',
];

export const QUIET_OFF_PHRASES = [
  'turn answers back on',
  'turn answers on',
  'add detail',
  'verbose mode',
  'full answers',
];

const LS_KEY = 'searchinc_quiet_mode';
const LS_FIRST_TIME_TIP_KEY = 'searchinc_quiet_mode_tip_seen';

// Operator-approved verbatim copy — DO NOT rephrase without operator approval.
export const COPY = Object.freeze({
  firstTimeTip:
    '💡 Just want links — no AI summary? Type "stop talking so much" anytime. Type "turn answers back on" to switch back.',
  quietOnToast:
    '🔇 Quiet mode on — no AI summaries. Type "turn answers back on" to switch back.',
  quietOffToast:
    '🔊 AI answers back on. Type "stop talking so much" to switch back.',
  chipLabel: '🔇 Quiet mode · click to switch back',
});

/**
 * Returns the user's current quiet-mode preference (boolean).
 * Defaults to false (verbose, default behaviour) for first-time users.
 */
export function getQuietMode() {
  try {
    return localStorage.getItem(LS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Persist the user's preference. Caller is responsible for showing
 * the confirmation toast — this function only does storage.
 */
export function setQuietMode(value) {
  try {
    if (value) {
      localStorage.setItem(LS_KEY, '1');
    } else {
      // Use removeItem rather than '0' so existing users default cleanly
      // when the schema evolves.
      localStorage.removeItem(LS_KEY);
    }
  } catch {
    /* localStorage unavailable — fail silently, mode persists in memory only */
  }
}

/**
 * Has the user seen the first-time tip yet?
 */
export function hasSeenFirstTimeTip() {
  try {
    return localStorage.getItem(LS_FIRST_TIME_TIP_KEY) === '1';
  } catch {
    return true; // fail closed — don't pester user if storage broken
  }
}

export function markFirstTimeTipSeen() {
  try {
    localStorage.setItem(LS_FIRST_TIME_TIP_KEY, '1');
  } catch {
    /* noop */
  }
}

/**
 * Classify a raw user query into one of three intents:
 *   - 'quiet_on'  — user typed a phrase that switches quiet mode ON
 *   - 'quiet_off' — user typed a phrase that switches quiet mode OFF
 *   - null        — query is a real search; passes through untouched
 *
 * Matching rules:
 *   1. Lowercase + trim whitespace.
 *   2. FULL-STRING match only — phrase must equal the entire trimmed query.
 *      This prevents false-positives like "Economic Forecast 2026. Add Detail"
 *      which contains 'add detail' as a suffix but is a real search.
 *   3. Punctuation matters: a trailing period prevents match (we don't
 *      strip punctuation, by design — keeps the rule predictable).
 */
export function classifyCommand(rawQuery) {
  if (typeof rawQuery !== 'string') return null;
  const normalised = rawQuery.trim().toLowerCase();
  if (!normalised) return null;
  if (QUIET_ON_PHRASES.includes(normalised)) return 'quiet_on';
  if (QUIET_OFF_PHRASES.includes(normalised)) return 'quiet_off';
  return null;
}

// Test hook: clears all quiet-mode preferences. Used only by Jest/Playwright.
export function _resetForTests() {
  try {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LS_FIRST_TIME_TIP_KEY);
  } catch {
    /* noop */
  }
}
